import { api } from "./client";

// ВСИЧКИ настройки, които FE-то чете, идват на едно място: `GET /user/info`
// → `configuration` (пази се в Redux, `auth.user.configuration`). Тук са
// типовете и ЗАПИСВАЩИТЕ заявки — всеки PUT връща само своята част, а hook-ът
// (`hooks/useConfiguration.ts`) я слива обратно в store-а.
//
// Две групи:
// - на РЕСТОРАНТА (таблица `configuration` в базата на ресторанта, общи за
//   всички терминали; сменят се от Конфигурация → Маркиране с правото
//   ordering-settings): nonFiscalDisplayMode, orderingQuickClose,
//   orderingSharedNumbering, orderingTableOrder, orderingSuccessNotifications,
//   catalogFilter, colorBy, orderingSearchField, orderingSearchScope,
//   orderingGuestCount, orderingSuggestTableName, orderingAutoCodeLogout,
//   orderingAutoCodeLogoutSeconds, orderingCashChange;
//   + workingHours (Конфигурация → Работно време, същото право);
// - ЛИЧНИ на потребителя (org таблица `user_configuration`, следват го на
//   всяко устройство; сменят се САМО от самия модул, не от Конфигурация —
//   сервитьорът може да няма права до нея): catalogView (Плочки/Таблица във футъра), keyboardLayout (БДС|Фонетична|ABC
//   на екранната клавиатура), topFilterExpanded (ъгълчето „Всички" над
//   каталога), sideFilterExpanded (същото за филтъра вдясно над прегледа),
//   catalogSort (бутонът АБВ/КОД във футъра), footerActions (влачене между
//   „Опции“ и лентата под масите), tablesView (Списък/Схема в горната лента
//   на масите). Нищо от тях не се пази
//   в localStorage — всяка смяна е PUT и при следващо влизане се помни.
// - theme е ИЗКЛЮЧЕНИЕ: темата е на компютъра (localStorage, lib/theme.ts) и
//   полето в базата се чете само веднъж — за да се пренесе досегашният избор
//   на компютър без записана тема (UserConfigurationSync); FE вече не го пише.
// Енъмите са огледало на BE схемите (app/features/configuration/schemas.py и
// app/external/user_configuration/schemas.py).

/** Дали закритите нефискализирани маси остават видими в списъка. */
export type NonFiscalDisplayMode = "never" | "always" | "on_demand";

/** Тема на интерфейса — цвят + светла/тъмна; сменя се САМО от профилното меню и
 * от бутона на ПИН екрана, не от Конфигурация. "light"/"dark" са оранжевият цвят
 * (заварени ключове), останалите — "<цвят>" и "<цвят>-dark" от
 * `styles/palettes/` (lib/themePalettes.ts ги разлага). */
export type ThemeMode =
  | "light"
  | "dark"
  | "green"
  | "green-dark"
  | "sky"
  | "sky-dark"
  | "cream"
  | "cream-dark"
  | "violet"
  | "violet-dark"
  | "silver"
  | "silver-dark"
  | "red"
  | "red-dark";

/** Изглед на продуктовия каталог в „Маркиране": редове или карти в решетка. */
export type CatalogView = "list" | "grid";

/** Масите в „Маркиране": списък с карти или върху схемата на заведението
 * (`pages/ordering/components/tables/schema/`). */
export type TablesView = "list" | "schema";

/** Кое стои над каталога — групите (станциите отиват над поръчаното) или
 * станциите (групите отиват над поръчаното). */
export type CatalogFilter = "groups" | "stations";

/** По какво се оцветяват продуктите в „Маркиране". */
export type ColorBy = "group" | "station" | "none";

/** Къде търси търсачката на каталога при избрана група: "menu" — в цялото
 * меню (първата буква излиза от групата); "group" — само в групата и след
 * добавяне се остава в нея. */
export type OrderingSearchScope = "menu" | "group";

/** „Въвеждане на брой гости" в „Маркиране": "off" — не пита; "new_table" —
 * пита при „Нова маса", преди модала за името; "close" — пита при „Закрий",
 * преди модала за закриване (`pages/ordering/components/tables/guests/`). */
export type OrderingGuestCount = "off" | "new_table" | "close";

/** Подредба на физическата клавиатура: БДС, фонетична или латиница. */
export type KeyboardLayout = "bds" | "phonetic" | "latin";

/** Подредба на асортиментите/рецептите в каталога на „Маркиране": по име
 * (на текущия език), по PLU код — без код → накрая, по име, или по сорт индекс
 * — при еднакъв по име, без индекс → накрая, по име
 * (`components/menu/catalogSort.ts`). Същите стойности като BE `CatalogSort`. */
export type CatalogSort = "alphabetical" | "plu" | "sort_index";

/** Подредба на масите в списъка на „Маркиране": по време на отваряне или
 * първо числовите имена по номер, после именуваните по азбучен ред
 * (`components/tables/tableOrder.ts`). */
export type OrderingTableOrder = "chronological" | "alphanumeric";

/** Една смяна от работния ден. Часовете са "HH:MM" през 15 мин. */
export interface WorkingShift {
  opensAt: string;
  closesAt: string;
}

/** Един ден от работното време — от една до три смени по реда им, една след
 * друга или с почивка между тях. Часовете вървят напред: час, по-ранен от
 * предишния, е след полунощ (10:00–02:00). Почивният ден пази смените си, за
 * да се върнат при отмяна на почивката. */
export interface WorkingDay {
  closed: boolean;
  shifts: WorkingShift[];
}

export interface RestaurantConfiguration {
  nonFiscalDisplayMode: NonFiscalDisplayMode;
  /** След „Поръчай" се отваря „Закрий", „Нова маса" взима най-малкия свободен номер. */
  orderingQuickClose: boolean;
  /** Включена → номерата на масите са общи за ресторанта; изключена → лична номерация. */
  orderingSharedNumbering: boolean;
  /** Как са подредени масите в списъка — хронологично или по номер/име. */
  orderingTableOrder: OrderingTableOrder;
  /** Включени → зелените toast-ове за успех в „Маркиране" се показват;
   * изключени → само те се спират (`pages/ordering/components/useOrderingMessage.ts`),
   * грешките и предупрежденията остават винаги. */
  orderingSuccessNotifications: boolean;
  /** Кое стои над каталога — групите или станциите. */
  catalogFilter: CatalogFilter;
  /** По какво се оцветяват редовете/картите и лентата на реда в прегледа. */
  colorBy: ColorBy;
  /** Включено → на десктоп каталогът показва полето за търсене (`ItemCatalog`);
   * изключено (по подразбиране) → полето е скрито; написаното личи по оцветените съвпадения.
   * На телефон/таблет полето е винаги видимо. */
  orderingSearchField: boolean;
  /** Къде търси търсачката при избрана група (`ItemCatalog`) — в „Маркиране"
   * и в редактора на обедно меню еднакво. */
  orderingSearchScope: OrderingSearchScope;
  /** Дали и кога „Маркиране" пита за броя гости — попита ли, без брой
   * следващият модал („Нова маса" / „Закрий") не се отваря. */
  orderingGuestCount: OrderingGuestCount;
  /** Включено → модалът „Нова маса" се отваря с първия свободен номер от
   * `GET /bills/next-table-name` (`tables/manage/useNewTableNameInput.ts`);
   * първата въведена цифра го заменя. Изключено (по подразбиране) → празен.
   * При orderingQuickClose модал няма и настройката не участва. */
  orderingSuggestTableName: boolean;
  /** Включено → служител, влязъл с КОД на общ терминал, се връща на екрана
   * за код след orderingAutoCodeLogoutSeconds без действие
   * (`routes/useAutoCodeLogout.ts`). Вход с име и парола не се засяга.
   * Изключено (по подразбиране) → остава влязъл. */
  orderingAutoCodeLogout: boolean;
  /** Секундите без действие за orderingAutoCodeLogout (BE приема 10–3600,
   * по подразбиране 30; полето в Конфигурация приема същите граници). */
  orderingAutoCodeLogoutSeconds: number;
  /** Включено (по подразбиране) → при „В брой" модалът „Закрий" показва
   * „Получено" и „Ресто"; изключено → „В брой" изглежда като „С карта".
   * Рестото е само за плащане в брой. Чете се през `useCashChangeEnabled`
   * (`hooks/useConfiguration.ts`), който третира липсващото поле (по-стар BE
   * или кеш на самоличността) като включено. */
  orderingCashChange: boolean;
  /** Работното време — седем дни, понеделник първи; null — още не е
   * настроено. Ползва се САМО за часовете в справките с час (периодът по
   * подразбиране — `lib/businessDay.ts`, менюто „смяна" — `lib/reportShift.ts`)
   * и за часовете на формата за резервации
   * (`pages/reservations/logic/workingSlots.ts`). */
  workingHours: WorkingDay[] | null;
}

export interface UserConfiguration {
  theme: ThemeMode;
  catalogView: CatalogView;
  keyboardLayout: KeyboardLayout;
  /** Разгънат ли е ГОРНИЯТ филтър (редът над каталога). Имената са по
   * мястото на реда — какво стои горе решава catalogFilter. */
  topFilterExpanded: boolean;
  /** Разгънат ли е ДЕСНИЯТ филтър (над прегледа на поръчката), където стои
   * другото — независим от topFilterExpanded. */
  sideFilterExpanded: boolean;
  /** Как са подредени продуктите в каталога — по име, по PLU код или по сорт
   * индекс. */
  catalogSort: CatalogSort;
  /** Целият ред на лентата под масите: ключовете на основните бутони
   * (`newTable`, `addToTable`, `closeTable`, `options`) и на довлечените от
   * „Опции“ действия (`components/tables/footer/footerPinning.ts`). Ключ без
   * право се прескача; при теснота бутоните се свиват, не отпадат. */
  footerActions: string[];
  /** Масите като списък или върху схемата — превключвателят Списък/Схема в
   * горната лента (само на десктоп; под него екранът е винаги Списък). */
  tablesView: TablesView;
}

export type Configuration = RestaurantConfiguration & UserConfiguration;

export type UserConfigurationUpdate = Partial<UserConfiguration>;

/** Стойностите по подразбиране — същите като BE default-ите. Ползват се,
 * докато `/user/info` още не е зареден (и на ПИН екрана без служител). */
export const DEFAULT_CONFIGURATION: Configuration = {
  nonFiscalDisplayMode: "never",
  orderingQuickClose: false,
  orderingSharedNumbering: true,
  orderingTableOrder: "chronological",
  orderingSuccessNotifications: true,
  catalogFilter: "groups",
  colorBy: "group",
  orderingSearchField: false,
  orderingSearchScope: "menu",
  orderingGuestCount: "off",
  orderingSuggestTableName: false,
  orderingAutoCodeLogout: false,
  orderingAutoCodeLogoutSeconds: 30,
  orderingCashChange: true,
  workingHours: null,
  theme: "light",
  catalogView: "list",
  keyboardLayout: "phonetic",
  topFilterExpanded: false,
  sideFilterExpanded: false,
  catalogSort: "alphabetical",
  footerActions: [],
  tablesView: "list",
};

export const updateNonFiscalDisplay = async (
  mode: NonFiscalDisplayMode,
): Promise<{ mode: NonFiscalDisplayMode }> => {
  const response = await api.put<{ mode: NonFiscalDisplayMode }>(
    "/configuration/non-fiscal-display",
    { mode },
  );
  return response.data;
};

export const updateOrderingQuickClose = async (
  enabled: boolean,
): Promise<{ enabled: boolean }> => {
  const response = await api.put<{ enabled: boolean }>(
    "/configuration/ordering-quick-close",
    { enabled },
  );
  return response.data;
};

export const updateOrderingSharedNumbering = async (
  enabled: boolean,
): Promise<{ enabled: boolean }> => {
  const response = await api.put<{ enabled: boolean }>(
    "/configuration/ordering-shared-numbering",
    { enabled },
  );
  return response.data;
};

export const updateOrderingTableOrder = async (
  order: OrderingTableOrder,
): Promise<{ order: OrderingTableOrder }> => {
  const response = await api.put<{ order: OrderingTableOrder }>(
    "/configuration/ordering-table-order",
    { order },
  );
  return response.data;
};

export const updateOrderingSuccessNotifications = async (
  enabled: boolean,
): Promise<{ enabled: boolean }> => {
  const response = await api.put<{ enabled: boolean }>(
    "/configuration/ordering-success-notifications",
    { enabled },
  );
  return response.data;
};

export const updateOrderingSearchField = async (
  enabled: boolean,
): Promise<{ enabled: boolean }> => {
  const response = await api.put<{ enabled: boolean }>(
    "/configuration/ordering-search-field",
    { enabled },
  );
  return response.data;
};

export const updateOrderingSearchScope = async (
  scope: OrderingSearchScope,
): Promise<{ scope: OrderingSearchScope }> => {
  const response = await api.put<{ scope: OrderingSearchScope }>(
    "/configuration/ordering-search-scope",
    { scope },
  );
  return response.data;
};

export const updateOrderingGuestCount = async (
  mode: OrderingGuestCount,
): Promise<{ mode: OrderingGuestCount }> => {
  const response = await api.put<{ mode: OrderingGuestCount }>(
    "/configuration/ordering-guest-count",
    { mode },
  );
  return response.data;
};

export const updateOrderingSuggestTableName = async (
  enabled: boolean,
): Promise<{ enabled: boolean }> => {
  const response = await api.put<{ enabled: boolean }>(
    "/configuration/ordering-suggest-table-name",
    { enabled },
  );
  return response.data;
};

export const updateOrderingAutoCodeLogout = async (
  enabled: boolean,
): Promise<{ enabled: boolean }> => {
  const response = await api.put<{ enabled: boolean }>(
    "/configuration/ordering-auto-code-logout",
    { enabled },
  );
  return response.data;
};

export const updateOrderingAutoCodeLogoutSeconds = async (
  seconds: number,
): Promise<{ seconds: number }> => {
  const response = await api.put<{ seconds: number }>(
    "/configuration/ordering-auto-code-logout-seconds",
    { seconds },
  );
  return response.data;
};

export const updateOrderingCashChange = async (
  enabled: boolean,
): Promise<{ enabled: boolean }> => {
  const response = await api.put<{ enabled: boolean }>(
    "/configuration/ordering-cash-change",
    { enabled },
  );
  return response.data;
};

export const updateOrderingCatalogFilter = async (
  catalogFilter: CatalogFilter,
): Promise<{ catalogFilter: CatalogFilter }> => {
  const response = await api.put<{ catalogFilter: CatalogFilter }>(
    "/configuration/ordering-catalog-filter",
    { catalogFilter },
  );
  return response.data;
};

export const updateOrderingColorBy = async (
  colorBy: ColorBy,
): Promise<{ colorBy: ColorBy }> => {
  const response = await api.put<{ colorBy: ColorBy }>(
    "/configuration/ordering-color-by",
    { colorBy },
  );
  return response.data;
};

export const updateWorkingHours = async (
  workingHours: WorkingDay[],
): Promise<{ workingHours: WorkingDay[] | null }> => {
  const response = await api.put<{ workingHours: WorkingDay[] | null }>(
    "/configuration/working-hours",
    { workingHours },
  );
  return response.data;
};

/** Частично обновяване на личните настройки — праща се само промененото. */
export const updateUserConfiguration = async (
  data: UserConfigurationUpdate,
): Promise<UserConfiguration> => {
  const response = await api.put<UserConfiguration>("/configuration/user", data);
  return response.data;
};
